import type { GetStaticProps, NextPage } from 'next';
import { useContext, useEffect, useState } from 'react';
import { CouponButton } from 'src/components/pages/campaign/bank/yamazaki202504/CouponButton';
import { PrizeContent } from 'src/components/pages/campaign/bank/yamazaki202504/PrizeContent';
import { PromotionDetails } from 'src/components/pages/campaign/bank/yamazaki202504/PromotionDetails';
import { ArrowIcon } from 'src/components/shared/campaign/ArrowIcon';
import { CampaignDetails } from 'src/components/shared/campaign/CampaignDetails';
import { CouponDetail } from 'src/components/shared/campaign/CouponDetail';
import { Hero } from 'src/components/shared/campaign/Hero';
import { LocalePrecautions } from 'src/components/shared/campaign/LocalePrecautions';
import { HtmlHead } from 'src/components/shared/meta/HtmlHead';
import { CLOUD_FRONT_IMAGE_PATH } from 'src/constants/imagePath';
import { useAddCoupon } from 'src/hooks/useAddCoupon';
import { useCoupon } from 'src/hooks/useCoupon';
import { useModal } from 'src/hooks/useModal';
import { I18nContext } from 'src/stores/I18n';
import { LOCALE_TYPE } from 'src/types/locale';
import { colors } from 'styles/colors';

export const YAMAZAKI_GIFT_202504_COUPON_CODE = '3YU4E2';
export const YAMAZAKI_GIFT_202504_URL = '/yamazaki-gift-202504';
export const YAMAZAKI_GIFT_202504_IMAGE_BASE_PATH = `${CLOUD_FRONT_IMAGE_PATH}/campaign/gift/yamazaki202504`;
export const YAMAZAKI_GIFT_202504_APP_DOWNLOAD_URL = 'https://app.adjust.com/1mmhjoed';

type Props = {
  locale: LOCALE_TYPE;
};

const YamazakiGift202504CampaignPage: NextPage<Props> = ({ locale }) => {
  const coupon = {
    promoCode: YAMAZAKI_GIFT_202504_COUPON_CODE,
    isAcquired: false
  };

  const localeResources = useContext(I18nContext);
  const { coupons: couponHistories, mutate } = useCoupon();
  const { execAddCouponByCode } = useAddCoupon();
  const acquiredCouponCodes = couponHistories?.map((couponHistory) => couponHistory.coupon.promoCode);
  const [isAlreadyAcquired, setIsAlreadyAcquired] = useState<boolean>(coupon.isAcquired);
  const { setIsVisible: setIssuedCouponModalVisible } = useModal('issuedCouponModal');

  useEffect(() => {
    setIsAlreadyAcquired(acquiredCouponCodes?.includes(YAMAZAKI_GIFT_202504_COUPON_CODE) ?? false);
  }, [acquiredCouponCodes, couponHistories]);

  const addCoupon = async () => {
    if (!isAlreadyAcquired && coupon.promoCode) {
      await execAddCouponByCode(coupon.promoCode);
      await mutate();
    }
  };

  const handleIssuedCouponModalOpen = () => {
    setIssuedCouponModalVisible(true);
  };

  return (
    <main className="font-noto text-white">
      <HtmlHead
        robotsIndex
        path="/campaign/yamazaki-gift-202504"
        title={localeResources.yamazakiGift.meta.title}
        description={localeResources.yamazakiGift.promotionDetails}
      />
      <Hero
        height="h-auto"
        mobileAspectRatio="aspect-[390/600]"
        desktopAspectRatio="aspect-[1440/720]"
        mobileImageUrl={`${YAMAZAKI_GIFT_202504_IMAGE_BASE_PATH}/hero/mobile/${locale}.webp`}
        desktopImageUrl={`${YAMAZAKI_GIFT_202504_IMAGE_BASE_PATH}/hero/desktop/${locale}.webp`}
        mobileBackgroundSize="bg-contain"
      />
      <div
        className="flex flex-col gap-10 pt-20 pb-10 bg-no-repeat bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.8), rgba(0,0,0,0.8)), url(${`${YAMAZAKI_GIFT_202504_IMAGE_BASE_PATH}/bg.webp`})`
        }}
      >
        <PromotionDetails
          titleImageUrl={`${YAMAZAKI_GIFT_202504_IMAGE_BASE_PATH}/promotion-details/title/${locale}.webp`}
          text={localeResources.yamazakiGift.promotionDetails}
          textContainerBorderColor="border-yamazakiOrange"
        />
        <CampaignDetails
          periodProps={{
            periodTitleImageUrl: `${YAMAZAKI_GIFT_202504_IMAGE_BASE_PATH}/period/title/${locale}.webp`,
            periodText: localeResources.yamazakiGift.period,
            periodTextContainerBorderColor: 'border-yamazakiOrange',
            isLineWrap: true
          }}
          conditionProps={{
            conditionTitleImageUrl: `${YAMAZAKI_GIFT_202504_IMAGE_BASE_PATH}/conditions/title/${locale}.webp`,
            conditionCompletionText: '',
            conditionsFlexDirection: 'flex-row',
            conditionTexts: [
              localeResources.yamazakiGift.conditions.text1,
              localeResources.yamazakiGift.conditions.text2
            ],
            conditionTextsColor: 'text-gray800',
            conditionTextContainerHeight: 'h-[128px]',
            conditionTextContainerBorderColor: 'border-yamazakiOrange',
            conditionPlusIconImageUrl: `${YAMAZAKI_GIFT_202504_IMAGE_BASE_PATH}/conditions/plus-icon.webp`,
            conditionTextContainerBackground: `${colors.white}`
          }}
          nextPageIcon={<ArrowIcon backgroundColor={colors.yamazakiOrange} />}
          content={
            <div className="flex flex-col justify-center gap-5 semiLg:flex-row semiLg:gap-16">
              <CouponButton
                text={
                  isAlreadyAcquired
                    ? localeResources.yamazakiGift.entryBotton.text1
                    : localeResources.yamazakiGift.entryBotton.text4
                }
                extraText={localeResources.yamazakiGift.entryBotton.text2}
                handleClick={addCoupon}
                isDisabled={isAlreadyAcquired}
              />
              {isAlreadyAcquired && (
                <CouponButton
                  text={localeResources.yamazakiGift.entryBotton.text3}
                  handleClick={handleIssuedCouponModalOpen}
                  isDisabled={false}
                />
              )}
            </div>
          }
        />
      </div>
      <CouponDetail
        backgroundColor="bg-yamazakiGray"
        paddingY="py-20"
        titleImage={{
          url: `${YAMAZAKI_GIFT_202504_IMAGE_BASE_PATH}/prize/title/${locale}.webp`,
          width: 232,
          height: 56
        }}
        gap={{
          mobile: 'gap-5',
          desktop: 'gap-10'
        }}
        content={<PrizeContent locale={locale} localeResources={localeResources} />}
      />
      <LocalePrecautions
        color="text-white"
        backgroundColor="bg-black"
        titleImageUrl={`${YAMAZAKI_GIFT_202504_IMAGE_BASE_PATH}/notes/title/${locale}.webp`}
        texts={Array.from(
          { length: 9 },
          (_, i) => (localeResources.yamazakiGift.notes as Record<string, string>)[`list${i + 1}`]
        )}
      />
    </main>
  );
};

export default YamazakiGift202504CampaignPage;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: { locale: locale ?? LOCALE_TYPE.TW, isSideMenuDisabled: true, isCouponBannersDisabled: true }
  };
};
